import React from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Separator } from '@swyftx/aviary/atoms/Seperator/Seperator';
import { Tag } from '@swyftx/aviary/atoms/Tag';

import { cn } from '@shared/utils/lib/ui';

import { useMarginAccount } from 'src/lib/margin/hooks/account';

import { MarginAccountSummaryActions } from './MarginAccountSummaryActions';
import { MarginAccountSummaryEquity } from './MarginAccountSummaryEquity';
import { MarginAccountCustomItem, MarginAccountItem } from '../../General/MarginAccountItem';
import { MarginAccountHealthMarginLevel } from '../MarginHealth/MarginAccountHealthMarginLevel';
import { MarginAccountHealthMarginStatus } from '../MarginHealth/MarginAccountHealthMarginStatus';

type Props = {
  className?: string;
  hideTradeButton?: boolean;
  showAccountHealthInfo?: boolean;
};

const MarginAccountSummary: React.FC<Props> = ({ hideTradeButton, showAccountHealthInfo, className }) => {
  const { t } = useTranslation('margin');
  const { account } = useMarginAccount();

  return (
    <>
      <FlexLayout alignItems='start' justifyContent='space-between' className={cn('flex-wrap', className)} spacing={24}>
        <FlexLayout
          direction='column'
          alignItems='start'
          justifyContent='space-between'
          spacing={24}
          className='w-full md:w-9/12'
        >
          <MarginAccountSummaryEquity hideProfitLoss />
          <FlexLayout spacing={24} alignItems='start' justifyContent='start' className='w-full flex-wrap'>
            <FlexLayout
              spacing={24}
              alignItems='start'
              justifyContent='start'
              className='order-1 w-[calc(50%-16px)] @sm:w-auto @lg:w-auto'
            >
              <MarginAccountItem
                title={t('accountOverview.profitLoss.title')}
                tooltip={t('accountOverview.profitLoss.tooltip')}
                dataKey='profitOpen'
                dynamicColor
                size='large'
              />
              <Separator className='hidden h-42 w-1 @sm:block' />
            </FlexLayout>

            <FlexLayout
              spacing={24}
              alignItems='start'
              justifyContent='start'
              className='order-4 w-[calc(50%-16px)] @sm:w-auto @lg:order-1 @lg:w-auto'
            >
              <MarginAccountItem
                title={t('accountOverview.balance.title')}
                tooltip={t('accountOverview.balance.tooltip')}
                dataKey='balance'
                size='large'
              />
              <Separator className='hidden h-42 w-1 @sm:block' />
            </FlexLayout>

            <FlexLayout
              spacing={24}
              alignItems='start'
              justifyContent='start'
              className='order-5 w-[calc(50%-16px)] @sm:w-auto @lg:order-1 @lg:w-auto'
            >
              <MarginAccountItem
                title={t('accountOverview.usedMargin.title')}
                tooltip={t('accountOverview.usedMargin.tooltip')}
                dataKey='margin'
                size='large'
              />
              <Separator className='hidden h-42 w-1 @sm:block' />
            </FlexLayout>

            <FlexLayout
              spacing={24}
              alignItems='start'
              justifyContent='start'
              className='order-6 w-[calc(50%-16px)] @sm:w-auto @lg:order-1 @lg:w-auto'
            >
              <MarginAccountItem
                title={t('accountOverview.availableMargin.title')}
                tooltip={t('accountOverview.availableMargin.tooltip')}
                dataKey='freeMargin'
                size='large'
              />
              <Separator className='hidden h-42 w-1 @sm:block' />
            </FlexLayout>

            <FlexLayout
              spacing={24}
              alignItems='start'
              justifyContent='start'
              className='order-6 w-[calc(50%-16px)] @sm:w-auto @lg:order-1 @lg:w-auto'
            >
              {account?.leverage && (
                <MarginAccountCustomItem
                  title={t('accountOverview.leverage.title')}
                  tooltip={t('accountOverview.leverage.tooltip')}
                  size='large'
                >
                  <Tag label={`${account?.leverage}x`} />
                </MarginAccountCustomItem>
              )}
              {showAccountHealthInfo && <Separator className='hidden h-42 w-1 @sm:block' />}
            </FlexLayout>

            {showAccountHealthInfo && (
              <FlexLayout
                spacing={24}
                alignItems='start'
                justifyContent='start'
                className='order-2 w-[calc(50%-16px)] @sm:w-auto @lg:order-1 @lg:w-auto'
              >
                <MarginAccountHealthMarginLevel content='bar' size='large' />
                <Separator className='hidden h-42 w-1 @sm:block' />
              </FlexLayout>
            )}

            {showAccountHealthInfo && (
              <MarginAccountHealthMarginStatus
                size='large'
                className='order-3 w-[calc(50%-16px)] @sm:w-auto @lg:order-1 @lg:w-auto'
              />
            )}
          </FlexLayout>
        </FlexLayout>

        <MarginAccountSummaryActions className='hidden sm:flex' hideTradeButton={hideTradeButton} />
      </FlexLayout>

      <div className='absolute bottom-0 left-0 z-10 w-full border-t border-color-border-main bg-color-background-surface-primary p-16 sm:hidden'>
        <MarginAccountSummaryActions hideTradeButton={hideTradeButton} />
      </div>
    </>
  );
};

export { MarginAccountSummary };
